import type { CellLayer, Fold, FoldAxis, Grid } from "../types.ts";

/**
 * Apply a fold to a grid, returning a new grid.
 * Pure function — no mutation.
 *
 * Fold line semantics:
 *   For vertical fold at line=L: the line is between col (L-1) and col L.
 *   For horizontal fold at line=L: the line is between row (L-1) and row L.
 *
 * Direction semantics:
 *   "left"  = cols [L..] fold left onto cols [0..L-1]
 *   "right" = cols [0..L-1] fold right onto cols [L..]
 *   "up"    = rows [L..] fold up onto rows [0..L-1]
 *   "down"  = rows [0..L-1] fold down onto rows [L..]
 */
export function applyFold(grid: Grid, fold: Fold): Grid {
  if (fold.axis === "vertical") {
    return applyVerticalFold(grid, fold.line, fold.direction as "left" | "right");
  } else {
    return applyHorizontalFold(grid, fold.line, fold.direction as "up" | "down");
  }
}

function applyVerticalFold(
  grid: Grid,
  line: number,
  direction: "left" | "right"
): Grid {
  const { rows, cols, cells } = grid;

  if (direction === "left") {
    // Stationary: cols [0..line-1], size = line
    // Folding:    cols [line..cols-1], size = cols - line
    const stationarySize = line;
    const foldingSize = cols - line;
    const newCols = Math.max(stationarySize, foldingSize);

    const newCells: CellLayer[][][] = [];
    for (let r = 0; r < rows; r++) {
      newCells[r] = [];
      for (let nc = 0; nc < newCols; nc++) {
        // Stationary column: align from the right (fold-line side)
        // Stationary col index in original grid
        const stCol = nc - (newCols - stationarySize);
        const baseStack: CellLayer[] =
          stCol >= 0 && stCol < stationarySize
            ? cells[r]![stCol]!.map((l) => ({ ...l }))
            : [];

        // Folding column: col closest to fold (line) maps to nc = newCols-1
        // col (line + k) maps to nc = (newCols - 1 - k)
        const foldOrigCol = line + (newCols - 1 - nc);
        if (foldOrigCol >= line && foldOrigCol < cols) {
          const foldedStack = [...cells[r]![foldOrigCol]!]
            .reverse()
            .map((l) => ({ ...l }));
          baseStack.push(...foldedStack);
        }

        newCells[r]![nc] = baseStack;
      }
    }

    return { rows, cols: newCols, cells: newCells };
  } else {
    // direction === "right"
    // Stationary: cols [line..cols-1], size = cols - line
    // Folding:    cols [0..line-1], size = line
    const stationarySize = cols - line;
    const foldingSize = line;
    const newCols = Math.max(stationarySize, foldingSize);

    const newCells: CellLayer[][][] = [];
    for (let r = 0; r < rows; r++) {
      newCells[r] = [];
      for (let nc = 0; nc < newCols; nc++) {
        // Stationary col in original grid: aligned from the left (fold-line side)
        const stCol = line + nc - (newCols - stationarySize);
        const baseStack: CellLayer[] =
          stCol >= line && stCol < cols
            ? cells[r]![stCol]!.map((l) => ({ ...l }))
            : [];

        // Folding: col (line-1) is closest to fold, maps to nc=0
        // col (line-1-k) maps to nc=k
        const foldOrigCol = (line - 1) - nc + (newCols - foldingSize);
        if (foldOrigCol >= 0 && foldOrigCol < line) {
          const foldedStack = [...cells[r]![foldOrigCol]!]
            .reverse()
            .map((l) => ({ ...l }));
          baseStack.push(...foldedStack);
        }

        newCells[r]![nc] = baseStack;
      }
    }

    return { rows, cols: newCols, cells: newCells };
  }
}

function applyHorizontalFold(
  grid: Grid,
  line: number,
  direction: "up" | "down"
): Grid {
  const { rows, cols, cells } = grid;

  if (direction === "up") {
    // Stationary: rows [0..line-1], size = line
    // Folding:    rows [line..rows-1], size = rows - line
    const stationarySize = line;
    const foldingSize = rows - line;
    const newRows = Math.max(stationarySize, foldingSize);

    const newCells: CellLayer[][][] = [];
    for (let nr = 0; nr < newRows; nr++) {
      newCells[nr] = [];
      for (let c = 0; c < cols; c++) {
        const stRow = nr - (newRows - stationarySize);
        const baseStack: CellLayer[] =
          stRow >= 0 && stRow < stationarySize
            ? cells[stRow]![c]!.map((l) => ({ ...l }))
            : [];

        const foldOrigRow = line + (newRows - 1 - nr);
        if (foldOrigRow >= line && foldOrigRow < rows) {
          const foldedStack = [...cells[foldOrigRow]![c]!]
            .reverse()
            .map((l) => ({ ...l }));
          baseStack.push(...foldedStack);
        }

        newCells[nr]![c] = baseStack;
      }
    }

    return { rows: newRows, cols, cells: newCells };
  } else {
    // direction === "down"
    // Stationary: rows [line..rows-1], size = rows - line
    // Folding:    rows [0..line-1], size = line
    const stationarySize = rows - line;
    const foldingSize = line;
    const newRows = Math.max(stationarySize, foldingSize);

    const newCells: CellLayer[][][] = [];
    for (let nr = 0; nr < newRows; nr++) {
      newCells[nr] = [];
      for (let c = 0; c < cols; c++) {
        const stRow = line + nr - (newRows - stationarySize);
        const baseStack: CellLayer[] =
          stRow >= line && stRow < rows
            ? cells[stRow]![c]!.map((l) => ({ ...l }))
            : [];

        const foldOrigRow = (line - 1) - nr + (newRows - foldingSize);
        if (foldOrigRow >= 0 && foldOrigRow < line) {
          const foldedStack = [...cells[foldOrigRow]![c]!]
            .reverse()
            .map((l) => ({ ...l }));
          baseStack.push(...foldedStack);
        }

        newCells[nr]![c] = baseStack;
      }
    }

    return { rows: newRows, cols, cells: newCells };
  }
}

export function getValidFolds(grid: Grid): Fold[] {
  const folds: Fold[] = [];
  for (let line = 1; line < grid.rows; line++) {
    folds.push({ axis: "horizontal", line, direction: "up" });
    folds.push({ axis: "horizontal", line, direction: "down" });
  }
  for (let line = 1; line < grid.cols; line++) {
    folds.push({ axis: "vertical", line, direction: "left" });
    folds.push({ axis: "vertical", line, direction: "right" });
  }
  return folds;
}
